/**
 * Gyroscope Bridge
 * 
 * Provides gyroscope/device motion data.
 * Full support via DeviceMotion API.
 */

import { StreamingBridge } from '../BaseBridge'
import type { BridgeAvailability, BridgeCapabilities, GyroscopeData } from '../types'

type DataCallback = (subscriptionId: string, data: any) => void

export class GyroscopeBridge extends StreamingBridge {
  readonly bridgeName = 'Gyroscope'
  readonly availability: BridgeAvailability = 'full'
  readonly methods = ['startListening', 'stopListening', 'getLastReading', 'setCallback']
  
  private lastReading: GyroscopeData = { x: 0, y: 0, z: 0, timestamp: 0 }
  private listening = false
  private motionHandler: ((event: DeviceMotionEvent) => void) | null = null
  private sendData: DataCallback
  
  constructor(sendData: DataCallback) {
    super()
    this.sendData = sendData
  }
  
  async handleCall(method: string, args: any[]): Promise<any> {
    switch (method) {
      case 'startListening':
        return this.startListeningInternal()
      case 'stopListening':
        return this.stopListeningInternal()
      case 'getLastReading':
        return JSON.stringify(this.lastReading)
      case 'setCallback':
        // Callback name not used in web implementation - data sent via postMessage
        void args[0]
        return true
      default:
        throw this.createMethodNotFoundError(method)
    }
  }
  
  private async startListeningInternal(): Promise<boolean> {
    if (this.listening) return true
    
    // Check if DeviceMotion is available
    if (!('DeviceMotionEvent' in window)) {
      throw this.createNotSupportedError('DeviceMotion')
    }
    
    // Request permission on iOS 13+
    if (typeof (DeviceMotionEvent as any).requestPermission === 'function') {
      try {
        const permission = await (DeviceMotionEvent as any).requestPermission()
        if (permission !== 'granted') {
          throw this.createPermissionDeniedError('motion sensors')
        }
      } catch (err: any) {
        if (err.code === 'PERMISSION_DENIED') throw err
        // Permission API not required on this device
      }
    }
    
    this.motionHandler = (event: DeviceMotionEvent) => {
      const rotationRate = event.rotationRate
      
      if (rotationRate) {
        this.lastReading = {
          x: rotationRate.alpha || 0,
          y: rotationRate.beta || 0,
          z: rotationRate.gamma || 0,
          timestamp: Date.now()
        }
        
        // Send to all subscriptions
        for (const subscriptionId of this.subscriptions.keys()) {
          this.sendData(subscriptionId, this.lastReading)
        }
      }
    }
    
    window.addEventListener('devicemotion', this.motionHandler)
    this.listening = true
    
    return true
  }
  
  private stopListeningInternal(): boolean {
    if (!this.listening) return true
    
    if (this.motionHandler) {
      window.removeEventListener('devicemotion', this.motionHandler)
      this.motionHandler = null
    }
    
    this.listening = false
    return true
  }
  
  startStreaming(subscriptionId: string, _options?: any): void {
    this.subscriptions.set(subscriptionId, true)
    
    if (!this.listening) {
      this.startListeningInternal().catch(console.error)
    }
  }
  
  stopStreaming(subscriptionId: string): void {
    this.subscriptions.delete(subscriptionId)
    
    if (this.subscriptions.size === 0) {
      this.stopListeningInternal()
    }
  }
  
  cleanup(): void {
    this.stopListeningInternal()
    super.cleanup()
  }
  
  getCapabilities(): BridgeCapabilities {
    const hasDeviceMotion = 'DeviceMotionEvent' in window
    
    return {
      startListening: { 
        available: hasDeviceMotion,
        requiresPermission: true
      },
      stopListening: { available: hasDeviceMotion },
      getLastReading: { available: hasDeviceMotion },
      setCallback: { available: true }
    }
  }
}

